import {DashboardEconomicSearchParams} from '@/features/dashboard/types'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {StyledDashboardEconomicFiltersContainer} from '@/features/dashboard/components/dashboard-economic-filters/style'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {AssistiveRegimeEnum} from '@/features/projects/types'
import {dashboardFiltersAssistiveRegimeToLabel, dashboardFiltersWorkSectorToLabel} from '../../utils'
import {DateRangeFilter} from '@/components/commons/date-range-filter/DateRangeFilter'
import {DateRangeFilterEnum} from '@/types'
import {useGetDashboardAnalytics} from '../../query/useGetDashboardAnalytics'
import {DashboardLastSyncedAt} from '../dashboard-last-synced-at/DashboardLastSyncedAt'

export const DashboardEconomicFilters = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useParsedSearchParams(DashboardEconomicSearchParams)
    const {data} = useGetDashboardAnalytics({fields: ['graph'], ...searchParams})

    const handleSectorChange = (value: keyof typeof dashboardFiltersWorkSectorToLabel) => {
        if (value === 'all') {
            setSearchParams({sector: undefined})
        } else {
            setSearchParams({sector: value as 'outpatient' | 'domiciliary'})
        }
    }

    const handleAssistiveRegimeChange = (value: keyof typeof dashboardFiltersAssistiveRegimeToLabel) => {
        if (value === 'all') {
            setSearchParams({assistiveRegime: undefined})
        } else {
            setSearchParams({assistiveRegime: value as AssistiveRegimeEnum})
        }
    }
    const handleApply = (values: {startDate: string; endDate: string; range: DateRangeFilterEnum}) => {
        setSearchParams(values)
    }

    return (
        <StyledDashboardEconomicFiltersContainer justify="space-between" align="center" gap={2}>
            <Flexbox align="center" gap={2}>
                <DateRangeFilter values={searchParams} onApply={handleApply} />

                <InputSelect
                    style={{width: 'auto'}}
                    sameWidth={false}
                    value={searchParams.sector || 'all'}
                    onClear={searchParams.sector && (() => setSearchParams({sector: undefined}))}
                    displayValue={value => {
                        return (
                            <Flexbox gap={1}>
                                <Text palette="neutral" shade="600" size="textSm">
                                    {t('commons:workSector')}:
                                </Text>
                                <Text palette="neutral" shade="950" size="textSm" weight={500}>
                                    {value}
                                </Text>
                            </Flexbox>
                        )
                    }}
                    onChange={handleSectorChange}
                    optionsMap={dashboardFiltersWorkSectorToLabel}
                    placeholder={t('table_filters:select_value')}
                />

                <InputSelect
                    style={{width: 'auto'}}
                    sameWidth={false}
                    value={searchParams.assistiveRegime || 'all'}
                    onClear={searchParams.assistiveRegime && (() => setSearchParams({assistiveRegime: undefined}))}
                    displayValue={value => {
                        return (
                            <Flexbox gap={1}>
                                <Text palette="neutral" shade="600" size="textSm">
                                    {t('commons:welfare_system')}:
                                </Text>
                                <Text palette="neutral" shade="950" size="textSm" weight={500}>
                                    {value}
                                </Text>
                            </Flexbox>
                        )
                    }}
                    onChange={handleAssistiveRegimeChange}
                    optionsMap={dashboardFiltersAssistiveRegimeToLabel}
                    placeholder={t('table_filters:select_value')}
                />
            </Flexbox>

            <DashboardLastSyncedAt lastSyncedAt={data?.lastSyncedAt} />
        </StyledDashboardEconomicFiltersContainer>
    )
}
